import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Per-page <title>, description, Open Graph tags and optional JSON-LD for
 * pages that know their own content (a product, a store, a category).
 *
 * SeoManager (siteContent.jsx) also writes these on every route change from
 * the admin's /seo table, and its request can land after the page has set
 * them. A page using this hook "claims" its path so SeoManager leaves it alone.
 * Client-side only: crawlers that do not run JS still see index.html.
 */
const claimed = new Set();

/** True while a mounted page owns the meta tags for `pathname`. */
export const isMetaClaimed = (pathname) => claimed.has(pathname);

const setMeta = (attr, key, value) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!value) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
};

const clip = (text, n = 160) => {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  return s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s;
};

export const SITE_NAME = 'LocalKonnect';

/**
 * `meta` is `{ title, description, image, jsonLd }`; pass null while the page
 * is still loading (the path is claimed straight away either way).
 */
export const useDocumentMeta = (meta) => {
  const { pathname } = useLocation();

  useEffect(() => {
    claimed.add(pathname);
    return () => {
      claimed.delete(pathname);
    };
  }, [pathname]);

  const title = meta?.title ? `${meta.title} | ${SITE_NAME}` : null;
  const description = meta?.description ? clip(meta.description) : null;
  const image = meta?.image || null;
  const jsonLd = meta?.jsonLd ? JSON.stringify(meta.jsonLd) : null;

  useEffect(() => {
    if (!title) return;
    document.title = title;
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:image', image);
  }, [title, description, image]);

  useEffect(() => {
    if (!jsonLd) return undefined;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.dataset.pageMeta = '1';
    el.textContent = jsonLd;
    document.head.appendChild(el);
    return () => el.remove();
  }, [jsonLd]);
};

export default useDocumentMeta;
